import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import LightCustomButton from "@components/buttons/LightCustomButton";
import GlobalSearchInputField from "@components/inputs/GlobalSearchInputField";
import { primeServices } from "@services/apis/Prime";
import { getApiErrorDisplay } from "@services/api/apiError";
import {
  GetPrimeTradesListRequest,
  GetPrimeTradesListResponse,
  PrimeTradeData,
} from "@services/models/Prime/GetPrimeTradeListModel";
import webSocketService from "@services/websocket/WebsocketServices";
import { RowClickedEvent } from "ag-grid-community";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import secureLocalStorage from "react-secure-storage";
import CreateTradeSlider from "./components/CreateTradeSlider";
import { createPrimeOrderColumnDefs } from "./components/PrimeOrderColumnDefs";
import PrimeDetailsTabs from "./PrimeDetailsTabs";
import { getPrimeOrderAgRowClass } from "./utils/primeOrderRowClass";

interface PrimeOrderFlowProps {
  countryFilter?: "IN" | "BR";
}

const PrimeOrderFlow = ({ countryFilter = "IN" }: PrimeOrderFlowProps) => {
  const gridRef = useRef<any>(null);

  const [orders, setOrders] = useState<PrimeTradeData[]>([]);
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tradeId, setTradeId] = useState<string | undefined>(undefined);
  const [tradeType, setTradeType] = useState<string[] | undefined>(undefined);
  const [tradeStatus, setTradeStatus] = useState<string[] | undefined>(
    undefined,
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [createTradeModalOpen, setCreateTradeModalOpen] =
    useState<boolean>(false);

  const ROWS_PER_PAGE = 2000;

  const [isPanelCollapsed, setIsPanelCollapsed] = useState<boolean>(false);

  const columnDefs = useMemo(
    () => createPrimeOrderColumnDefs(countryFilter),
    [countryFilter],
  );

  const didInitialFetch = useRef(false);
  const inFlight = useRef(false);

  const fetchPrimeOrders = async (pageNum: number, reset: boolean = false) => {
    if (inFlight.current) return;
    inFlight.current = true;

    const data: GetPrimeTradesListRequest = {
      tradeType: tradeType ? tradeType : undefined,
      tradeStatus: tradeStatus ? tradeStatus : undefined,
      page: pageNum,
      rows: ROWS_PER_PAGE,
      primeType: countryFilter,
    };

    try {
      setIsLoading(true);
      const response: GetPrimeTradesListResponse =
        await primeServices.getPrimeTradesList(data);

      if (response.data.length === 0) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }

      if (reset) {
        setOrders(response.data);
      } else {
        setOrders((prevOrders) => [...prevOrders, ...response.data]);
      }
    } catch (error: any) {
      setError(getApiErrorDisplay(error));
    } finally {
      setIsLoading(false);
      inFlight.current = false;
    }
  };

  useEffect(() => {
    if (!didInitialFetch.current) {
      didInitialFetch.current = true;
      setPage(1);
      setHasMore(true);
      fetchPrimeOrders(1, true);
    }
  }, []);

  useEffect(() => {
    if (!didInitialFetch.current) return;
    setPage(1);
    setHasMore(true);
    fetchPrimeOrders(1, true);
  }, [tradeType, tradeStatus]);

  useEffect(() => {
    if (page > 1 && hasMore && !searchTerm) {
      fetchPrimeOrders(page, false);
    }
  }, [page, hasMore, searchTerm]);

  const onBodyScroll = useCallback(
    (params: any) => {
      if (!hasMore || isLoading || searchTerm) return;

      const api = params.api;
      const displayedRowCount = api.getDisplayedRowCount();
      const lastDisplayedRowIndex = api.getLastDisplayedRowIndex();

      if (
        lastDisplayedRowIndex >= displayedRowCount - 5 &&
        displayedRowCount > 0
      ) {
        setPage((prev) => prev + 1);
      }
    },
    [hasMore, isLoading, searchTerm],
  );

  const onRowClicked = (event: RowClickedEvent) => {
    const selectedData = event.data as PrimeTradeData;

    const allRows = document.querySelectorAll(".ag-row");
    allRows.forEach((row) => {
      row.classList.remove("ag-row-selected-custom");
    });

    if (event.rowIndex !== null) {
      const rowElement = document.querySelector(
        `[row-index="${event.rowIndex}"]`,
      );
      if (rowElement) {
        rowElement.classList.add("ag-row-selected-custom");
      }
    }

    setTradeId(selectedData.tradeId.toString());
    setIsPanelCollapsed(false);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      if (gridRef.current && orders.length > 0 && !searchTerm) {
        gridRef.current.api.refreshCells({
          columns: ["active"],
          force: true,
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [orders.length, searchTerm]);

  useEffect(() => {
    const jwt = secureLocalStorage.getItem("jwt");
    webSocketService.connect({ token: jwt }, true);
    webSocketService.protectedSocket?.on("connect", () => {});
  }, []);

  const handleTradeChangeWebSockets = (socketData: PrimeTradeData) => {
    // Early return if search is active or country doesn't match
    if (searchTerm || socketData.countryCode !== countryFilter) {
      return;
    }

    // Check trade type filter
    if (tradeType && tradeType.length > 0) {
      const socketTradeType = socketData.tradeType === "B" ? "Buy" : "Sell";
      if (!tradeType.includes(socketTradeType)) {
        return;
      }
    }

    // Check trade status filter
    if (tradeStatus && tradeStatus.length > 0) {
      let currentStatus = "Pending";
      if (socketData.eventDetails && socketData.eventDetails.length > 0) {
        const sortedEvents = [...socketData.eventDetails].sort((a, b) => {
          return (
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
          );
        });

        const latestEvent = sortedEvents[0];
        switch (latestEvent.eventId) {
          case "1":
            currentStatus = "Received";
            break;
          case "2":
            currentStatus = "Accepted";
            break;
          case "3":
            currentStatus = "Confirmed";
            break;
          case "4":
          case "6":
            currentStatus = "Completed";
            break;
          case "5":
            currentStatus = "Disputed";
            break;
          case "9":
            currentStatus = "Expired";
            break;
          default:
            currentStatus = "Pending";
        }
      }

      if (!tradeStatus.includes(currentStatus)) {
        return;
      }
    }

    setOrders((prevOrders) => {
      const existingTradeIndex = prevOrders.findIndex(
        (order) => order.tradeId === socketData.tradeId,
      );

      if (existingTradeIndex !== -1) {
        const updatedOrders = [...prevOrders];
        updatedOrders[existingTradeIndex] = socketData;
        return updatedOrders;
      } else {
        if (prevOrders.length > 0) {
          const socketTradeId = Number(socketData.tradeId);
          const newestTradeId = Number(prevOrders[0].tradeId);

          if (socketTradeId > newestTradeId) {
            return [socketData, ...prevOrders];
          } else {
            return prevOrders;
          }
        }

        return [socketData, ...prevOrders];
      }
    });
  };

  useEffect(() => {
    const initializeWebSocket = async () => {
      try {
        webSocketService.protectedSocket?.off(
          "analytics_dashboard_trade_update",
        );

        if (!searchTerm) {
          webSocketService.onMessage(
            "analytics_dashboard_trade_update",
            true,
            handleTradeChangeWebSockets,
          );
        }
      } catch (error) {
        console.error("WebSocket initialization error:", error);
      }
    };
    initializeWebSocket();
  }, [
    webSocketService.protectedSocket?.connected,
    searchTerm,
    countryFilter,
    tradeType,
    tradeStatus,
  ]);

  const handleSearchSubmit = async () => {
    try {
      setIsLoading(true);
      const normalizedSearchTerm = searchTerm.trim();
      const response: GetPrimeTradesListResponse =
        await primeServices.searchPrimeTrade(
          normalizedSearchTerm,
          countryFilter,
        );
      setOrders(response.data);
      setHasMore(false);
    } catch (error: any) {
      setError(getApiErrorDisplay(error));
    } finally {
      setIsLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchTerm("");
    setPage(1);
    setHasMore(true);
    fetchPrimeOrders(1, true);
  };

  return (
    <>
      <div className="flex flex-col gap-[12px]">
        <div className="flex flex-row items-center justify-between pt-[20px]">
          <div className="flex flex-row items-center overflow-x-auto overflow-y-hidden">
            <div className="flex justify-start space-x-3 items-center w-full">
              <p className="text-text-base text-[10px] font-bold whitespace-nowrap">
                Filter by:
              </p>
              {countryFilter === "BR" && (
                <>
                  <div className="flex space-x-3">
                    {["Buy", "Sell"].map((type) => (
                      <div
                        key={type}
                        onClick={
                          isLoading
                            ? undefined
                            : () =>
                                setTradeType((prev) =>
                                  prev?.includes(type)
                                    ? prev.filter((s) => s !== type)
                                    : [...(prev || []), type],
                                )
                        }
                        className={`border py-[4px] px-[16px] text-text-base text-[10px] font-medium rounded-[100px] ${
                          isLoading
                            ? "opacity-50 cursor-not-allowed"
                            : "cursor-pointer"
                        } ${
                          tradeType?.includes(type)
                            ? "border-border-button-secondary bg-bg-button-secondary"
                            : "border-border-subtle bg-bg-page-body"
                        }`}
                      >
                        {type}
                      </div>
                    ))}
                  </div>
                  <div
                    style={{
                      height: "20px",
                      width: "2px",
                      backgroundColor: "#ccc",
                    }}
                  ></div>
                </>
              )}
              <div className="flex space-x-3">
                {[
                  "Received",
                  "Accepted",
                  "Confirmed",
                  "Completed",
                  ...(countryFilter !== "BR" ? ["Disputed"] : []),
                  "Expired",
                ].map((status) => (
                  <div
                    key={status}
                    onClick={
                      isLoading
                        ? undefined
                        : () =>
                            setTradeStatus((prev) =>
                              prev?.includes(status)
                                ? prev.filter((s) => s !== status)
                                : [...(prev || []), status],
                            )
                    }
                    className={`border py-[4px] px-[16px] text-text-base text-[10px] font-medium rounded-[100px] ${
                      isLoading
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                    } ${
                      tradeStatus?.includes(status)
                        ? "border-border-button-secondary bg-bg-button-secondary"
                        : "border-border-subtle bg-bg-page-body"
                    }`}
                  >
                    {status}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex flex-row items-center overflow-x-auto overflow-y-hidden px-[12px] gap-4">
            {countryFilter === "BR" && (
              <LightCustomButton
                text="CREATE TRADE"
                isActive={true}
                onClick={() => setCreateTradeModalOpen(true)}
              />
            )}
            <div className="w-[360px] flex-shrink-0">
              <GlobalSearchInputField
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onSearch={handleSearchSubmit}
                onClear={clearSearch}
                disabled={isLoading}
                isSearchApplied={!hasMore && Boolean(searchTerm.trim())}
                disableSearchButton={!searchTerm.trim()}
                placeholder="Search Order id, Trade id, UTR"
              />
            </div>
          </div>
        </div>
        <div className="flex flex-row items-start">
          <div
            className={`flex flex-col bg-bg-page-body py-[12px] pr-[12px] overflow-hidden ${
              tradeId && !isPanelCollapsed ? "w-3/4" : "w-full"
            }`}
            style={{ height: "calc(100vh - 200px)" }}
          >
            {isLoading && page == 1 ? (
              <AgGridSkeleton
                columnDefs={columnDefs}
                style={{ height: "100%", width: "100%" }}
              />
            ) : (
              <div className="flex flex-col h-full">
                <AgGridReactTable
                  ref={gridRef}
                  rowData={orders}
                  columnDefs={columnDefs}
                  onRowClicked={onRowClicked}
                  onBodyScroll={onBodyScroll}
                  getRowId={(params) => params.data.tradeId.toString()}
                  getRowClass={(params) =>
                    getPrimeOrderAgRowClass("trade", params.data)
                  }
                  error={error}
                />
                {isLoading && orders.length > 0 && (
                  <div className="flex items-center justify-center py-4 border-t border-border-subtle">
                    <div className="flex items-center space-x-2 text-text-subtle">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-border-subtle border-t-border-button-primary"></div>
                      <span className="text-sm">Loading more orders...</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          {tradeId && (
            <div
              className={`bg-bg-page-body border-l border-border-subtle transition-all duration-300 relative ${
                isPanelCollapsed ? "w-0 px-0" : "w-1/4 px-2"
              }`}
              style={{ height: "calc(100dvh - 70px)" }}
            >
              <button
                onClick={() => setIsPanelCollapsed(!isPanelCollapsed)}
                className="absolute top-1/2 -translate-y-1/2 z-20 bg-bg-button-secondary text-text-base rounded-lg shadow-lg transition-all duration-200 flex items-center justify-center group"
                style={{
                  left: isPanelCollapsed ? "calc(100% - 32px)" : "-16px",
                  width: "32px",
                  height: "60px",
                }}
                aria-label={
                  isPanelCollapsed ? "Expand panel" : "Collapse panel"
                }
              >
                <span className="material-symbols-outlined text-[20px] group-hover:scale-110 transition-transform">
                  {isPanelCollapsed ? "chevron_left" : "chevron_right"}
                </span>
              </button>

              <div
                className={`overflow-auto h-full transition-opacity duration-300 ${
                  isPanelCollapsed
                    ? "opacity-0 pointer-events-none"
                    : "opacity-100"
                }`}
              >
                <PrimeDetailsTabs tradeId={tradeId} />
              </div>
            </div>
          )}
        </div>
      </div>
      <CreateTradeSlider
        isOpen={createTradeModalOpen}
        onClose={() => setCreateTradeModalOpen(false)}
      />
    </>
  );
};

export default PrimeOrderFlow;
